import type { Project } from '@/api/client';
import { computeProjectProgress } from '@/utils/progress';
import { isProjectExcludedFromActiveTable, type ProjectStatus } from '@/utils/projectStatuses';
import {
  formatNamesShort,
  getDeadlineView,
  getListTypeMark,
  getStageChipLabel,
  getStageView,
  type DeadlineView,
  type StageView,
  type TypeMarkView,
} from '../model/projectView';
import { getListDeadline } from '../model/listView';

/** Срок в строке списка: срок текущего этапа, если задан, иначе общий (getListDeadline) */
export interface ListDeadlineView extends DeadlineView {
  /** «Завершить запись до» — в подсказке перед датой */
  label: string;
  /** Этап, чей срок показан, — его квадратик перед остатком; null — общий срок производства */
  stage: ProjectStatus | null;
}

function getListDeadlineView(project: Project): ListDeadlineView | null {
  const deadline = getListDeadline(project);
  const view = deadline && getDeadlineView(deadline.value, isProjectExcludedFromActiveTable(project.state));
  if (!deadline || !view) return null;
  return { ...view, label: deadline.label, stage: deadline.isStageDeadline ? deadline.stage : null };
}

/** Что показывает строка списка в обоих видах — карточкой и в одну строку. */
export interface ListRowView {
  stage: StageView;
  /** Текст бейджа подстатуса; null — бейджа нет */
  chip: string | null;
  deadline: ListDeadlineView | null;
  /** «Анна, Иван +2»; пусто — «…» (чтец не указан) */
  readers: string;
  /** Заметка к названию; null — нет */
  note: string | null;
  typeMark: TypeMarkView | null;
}

export interface ListRowOptions {
  /** Корешка этапа нет (по срочности, по алфавиту) — этап пишется в бейдже: «Правки · Подготовлены» */
  withStage?: boolean;
}

export function getListRowView(project: Project, { withStage = false }: ListRowOptions = {}): ListRowView {
  const stage = getStageView(project, computeProjectProgress(project));
  return {
    stage,
    chip: getStageChipLabel(stage, { withStage }),
    deadline: getListDeadlineView(project),
    readers: formatNamesShort(project.readers.map((r) => r.name), 2),
    note: project.name_note?.trim() || null,
    typeMark: getListTypeMark(project.project_type),
  };
}
